// @packages
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Grid from '@material-ui/core/Grid';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Typography from '@material-ui/core/Typography';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';

// @scripts
import CtrlSelectField from '../ctrl-select-field';
import CtrlMultiselectField from '../ctrl-multi-select-field';
import CtrlTextField from '../ctrl-text-field';

// @styles
import styles from './styles';

const inputTypeOption = {
    DATE: 'date',
    EMAIL: 'email',
    NAME: 'name',
    NUMERIC: 'numeric',
    PASSWORD: 'password',
    PHONE: 'phone',
    SELECT: 'select',
    TEXT: 'text',
    ZIP: 'zip',
    MULTISELECT: 'multiselect'
};

const CtrlAccordion = ({
    className,
    classes,
    defaultExpanded,
    fields,
    id,
    name,
    onChangeIncludeDriveLoss,
    onClickDriveLossMode,
    onFieldBlur,
    onFieldChange,
    onEnterClick,
    state,
    text
}) => {
    const [expanded, onExpandedChange] = useState(defaultExpanded);
    const accordionClass = classNames(
        className,
        classes.accordion
    );

    const handleOnExpandedChange = () => onExpandedChange(!expanded);

    const renderField = (fieldProps, index) => {
        let field = null;
        if (fieldProps.render) {
            field = fieldProps.render(state, onChangeIncludeDriveLoss, onClickDriveLossMode);
        } else {
            switch (fieldProps.type) {
                case inputTypeOption.DATE:
                case inputTypeOption.EMAIL:
                case inputTypeOption.NAME:
                case inputTypeOption.NUMERIC:
                case inputTypeOption.PASSWORD:
                case inputTypeOption.PHONE:
                case inputTypeOption.TEXT:
                case inputTypeOption.ZIP:
                default:
                    field = (
                        <CtrlTextField
                            id={`${id}-${fieldProps.name}`}
                            disabled={state[fieldProps.name].disabled}
                            key={index}
                            label={text[fieldProps.name] || fieldProps.name}
                            onBlur={onFieldBlur}
                            onChange={onFieldChange}
                            onEnter={onEnterClick}
                            value={state[fieldProps.name].value}
                            {...fieldProps}
                        />
                    );
                    break;
                case inputTypeOption.SELECT:
                    field = (
                        <CtrlSelectField
                            id={`${id}-${fieldProps.name}`}
                            key={index}
                            label={text[fieldProps.name] || fieldProps.name}
                            onBlur={onFieldBlur}
                            onChange={onFieldChange}
                            onEnter={onEnterClick}
                            value={state[fieldProps.name].value}
                            {...fieldProps}
                        />
                    );
                    break;
                case inputTypeOption.MULTISELECT:
                    field = (
                        <CtrlMultiselectField
                            id={`${id}-${fieldProps.name}`}
                            key={index}
                            label={text[fieldProps.name] || fieldProps.name}
                            onBlur={onFieldBlur}
                            onChange={onFieldChange}
                            onEnter={onEnterClick}
                            value={state[fieldProps.name].value}
                            {...fieldProps}
                        />
                    );
                    break;
            }
        }

        return (
            <Grid
                item
                key={index}
                lg={fieldProps.colspan.lg || 'auto'}
                md={fieldProps.colspan.md || 'auto'}
                sm={fieldProps.colspan.sm || 'auto'}
            >
                {field}
            </Grid>
        );
    };

    return (
        <Accordion id={id} className={accordionClass} expanded={expanded} onChange={handleOnExpandedChange}>
            <AccordionSummary className={classes.heading} expandIcon={<ExpandMoreIcon id={`${id}-expander`} />}>
                <Typography className={classes.titleHeading}>{text[name] || name}</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
                <Grid container spacing={3}>
                    {fields.map(renderField)}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};

CtrlAccordion.propTypes = {
    className: PropTypes.string,
    classes: PropTypes.object.isRequired,
    defaultExpanded: PropTypes.bool,
    fields: PropTypes.arrayOf(PropTypes.shape({
        decimals: PropTypes.number,
        disabled: PropTypes.bool,
        icon: PropTypes.string,
        maxLength: PropTypes.number,
        maxValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.number]),
        minLength: PropTypes.number,
        minValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.number]),
        name: PropTypes.string,
        showErrors: PropTypes.bool,
        type: PropTypes.oneOf([...Object.values(inputTypeOption)]),
        value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string])
    })).isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onClickDriveLossMode: PropTypes.func,
    onChangeIncludeDriveLoss: PropTypes.func,
    onFieldBlur: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    onEnterClick: PropTypes.func,
    text: PropTypes.object.isRequired,
    state: PropTypes.shape({
        showErrors: PropTypes.bool.isRequired
    }).isRequired
};

CtrlAccordion.defaultProps = {
    className: null,
    defaultExpanded: true,
    onClickDriveLossMode: null,
    onChangeIncludeDriveLoss: null,
    onEnterClick: null,
};

export default withStyles(styles)(CtrlAccordion);
